import { Button, ButtonGroup, Colors, Intent } from "@blueprintjs/core";
import {
    segmentTrackFavoritesFilterChanged,
    segmentTrackFavoritesPanelOpened
} from "analytics/analyticTrackEvents";
import FavoriteStar from "components/sidebar/BusItem/FavoriteStar";
import { useFavoriteBuses } from "contexts/FavoriteBusesContext/FavoriteBusesContext";
import React from "react";
import "./FavoritesFilter.scss";
import FilterWrapper from "./FilterWrapper";

const BUTTON_STAR_SIZE = 14;
const HELPER_SEPARATOR = "·";
const NO_FAVORITES_HELPER_TEXT =
    "Click a bus and star it to add it to your favorites.";

/** "1 favorite total" / "3 favorites total" */
export const getFavoritesTotalLabel = (count: number): string =>
    `${count} ${pluralFavorites(count)} total`;

/** "View all 1 favorite" / "View all 3 favorites" */
export const getViewAllLabel = (count: number): string =>
    `View all ${count} ${pluralFavorites(count)}`;

const pluralFavorites = (count: number): string =>
    count === 1 ? "favorite" : "favorites";

type FavoritesFilterProps = {
    // Narrow label and wide buttons in the sidebar, even columns in the All filters dialog.
    readonly inSidebar: boolean;
    // Lets the dialog close itself when the panel opens behind it.
    readonly onOpenPanel?: () => void;
};

// A segment that narrows the view, with the favorites count and a View all link
// that opens the panel underneath it.
const FavoritesFilter: React.FC<FavoritesFilterProps> = ({
    inSidebar,
    onOpenPanel
}) => {
    const {
        favorites,
        favoritesFilterOn,
        setFavoritesFilterOn,
        setFavoritesPanelOpen
    } = useFavoriteBuses();

    const count = favorites.length;
    // With nothing starred, "Favorites" hides every bus and the list falls
    // back to the generic "No search results" -- a blank map with no hint that
    // the favorites filter did it. Don't let the view get there. The label's
    // tooltip says how to get a first favorite.
    const hasNoFavorites = count === 0;

    const openPanel = (): void => {
        setFavoritesPanelOpen(true);
        segmentTrackFavoritesPanelOpened(
            inSidebar ? "sidebar" : "all_filters_dialog",
            count
        );
        if (onOpenPanel !== undefined) {
            onOpenPanel();
        }
    };

    const changeFilter = (on: boolean): void => {
        if (on === favoritesFilterOn) {
            return;
        }
        setFavoritesFilterOn(on);
        segmentTrackFavoritesFilterChanged(on, count);
    };

    const handleViewAllKeyDown = (event: React.KeyboardEvent): void => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            openPanel();
        }
    };

    const helper = (
        <div className="FavoritesFilter-helper bp5-text-small bp5-text-muted">
            <span className="FavoritesFilter-helper-count">
                {getFavoritesTotalLabel(count)}
            </span>
            <span aria-hidden={true}>{HELPER_SEPARATOR}</span>
            <span
                className="FavoritesFilter-view-all"
                role="button"
                tabIndex={0}
                aria-label={getViewAllLabel(count)}
                onClick={openPanel}
                onKeyDown={handleViewAllKeyDown}
            >
                View all
            </span>
        </div>
    );

    // Gold outline at rest, solid gold while the filter is on. A disabled
    // button keeps currentColor so the star fades with its text.
    const favoritesStar = (
        <FavoriteStar
            size={BUTTON_STAR_SIZE}
            filled={favoritesFilterOn}
            strokeColor={hasNoFavorites ? undefined : Colors.GOLD4}
        />
    );

    return (
        <FilterWrapper
            label="Buses"
            helperText={hasNoFavorites ? NO_FAVORITES_HELPER_TEXT : undefined}
            limitLabelMaxWidth={inSidebar}
            wideInput={inSidebar}
            footer={hasNoFavorites ? undefined : helper}
        >
            <ButtonGroup className="FavoritesFilter-segment">
                <Button
                    className="FavoritesFilter-segment-button"
                    text="All buses"
                    ellipsizeText={true}
                    active={!favoritesFilterOn}
                    intent={!favoritesFilterOn ? Intent.PRIMARY : Intent.NONE}
                    onClick={() => changeFilter(false)}
                />
                <Button
                    className="FavoritesFilter-segment-button"
                    icon={favoritesStar}
                    text="Favorites"
                    ellipsizeText={true}
                    active={favoritesFilterOn}
                    intent={favoritesFilterOn ? Intent.PRIMARY : Intent.NONE}
                    disabled={hasNoFavorites}
                    onClick={() => changeFilter(true)}
                />
            </ButtonGroup>
        </FilterWrapper>
    );
};

export default FavoritesFilter;
